import { useQuery, useQueryClient } from "@tanstack/react-query";
import { memo } from "react";

import { VenueDisplay } from "@/components/venues/display";
import type { Tournament, TournamentDivision, Venue } from "@/db/schema";
import type { LexicalState } from "@/db/schema/shared";
import { useTRPC } from "@/integrations/trpc/react";

export const InformationPanel = memo(function InformationPanel({
  id,
  description,
  imageSource,
  venue,
  isActive,
  divisionId,
  divisionDescription,
}: {
  id: Tournament["id"];
  description: LexicalState | null;
  imageSource: string | null;
  venue: Pick<
    Venue,
    | "id"
    | "name"
    | "city"
    | "description"
    | "directions"
    | "mapUrl"
    | "withdrawDeadlineHours"
    | "defaultAutopromoteWaitlist"
    | "registrationOpenDaysBefore"
  >;
  isActive: boolean;
  divisionId: TournamentDivision["id"];
  divisionDescription: LexicalState | null;
}) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { data: directors } = useQuery({
    ...trpc.tournaments.getDirectors.queryOptions({ tournamentId: id }),
    enabled: isActive,
  });

  if (!directors) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse px-3 pt-12">
        <div className="mb-4 h-5 w-32 rounded bg-gray-200" />
        <div className="flex flex-col gap-3 pb-12 sm:flex-row">
          <div className="flex-1 space-y-3">
            <div className="h-4 w-full rounded bg-gray-100" />
            <div className="h-4 w-5/6 rounded bg-gray-100" />
            <div className="h-4 w-4/6 rounded bg-gray-100" />
          </div>
          <div className="min-w-3/12 space-y-3">
            <div className="h-5 w-20 rounded bg-gray-200" />
            <div className="h-4 w-32 rounded bg-gray-100" />
            <div className="h-4 w-28 rounded bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <VenueDisplay
      tournamentId={id}
      tournamentDescription={description}
      tournamentImageSource={imageSource}
      divisionId={divisionId}
      divisionDescription={divisionDescription}
      venue={{
        ...venue,
        directors,
      }}
      onEditSuccess={() => {
        queryClient.invalidateQueries(trpc.tournaments.pathFilter());
      }}
    />
  );
});
